import { r as createLogger } from "../logger-CdXeOlrX.js";
import { isSessionJsonEntryName } from "./zip-reader.js";
import { rebuildZipWithEntries } from "./zip-rebuild.js";
import { BlobReader, TextWriter, ZipReader } from "@zip.js/zip.js";
//#region ../src/storage/zip-coverage-embed.ts
/**
* Zip Coverage Embed — write a recording's H3 coverage into its `session.json`.
*
* The map-centric browser indexes legacy recordings (those predating the
* `h3Cells` field) by reading their full GPS path — slow, and repeated on every
* folder open. This primitive lets a one-time "upgrade" embed the derived
* coverage **inside the recording's own `session.json`**, so the recording
* indexes instantly on every future open in this app *and any other reader*
* (the portability the user asked for; O3 — in-zip rewrite).
*
* It is a pure transform `(zip, cells, resolution) -> zip`: it reads
* `session.json`, decides whether there is anything to do, and re-emits the
* archive through `rebuildZipWithEntries` (the package's ONE re-emit loop,
* DEC-H3 — this module used to carry its own copy). It never mutates
* anything in place — the caller (the RecorderApp backfill) owns the safe
* write-then-verify-then-overwrite protocol around it — and it keeps its
* skip-and-return-input semantics on top of the rebuild's throw: a backfill
* over many recordings wants "left untouched", not an exception per file.
*
* @see ./zip-coverage-embed.ts.md
* @see GpsPlusSlamJs_Docs/docs/2026-06-14-1924-progressive-map-browser-indexing-and-backfill-followup.md (B3)
*/
const log = createLogger("ZipCoverageEmbed");
/**
* Return a new zip blob identical to `zip` except its `session.json` gains
* `h3Cells` + `h3Resolution`.
*
* **Skips (returns the input blob unchanged, by reference) when:**
*   - the zip has no `session.json` (nothing to embed into);
*   - `session.json` is present but unparseable (never write over a broken one);
*   - `session.json` already carries `h3Cells` (idempotent — re-running the
*     upgrade is a no-op, and new recordings are skipped).
*
* On any unexpected read/write failure it also returns the input untouched —
* it never emits a partial zip. Callers can detect a skip via reference
* equality (`result === zip`).
*
* Built on `rebuildZipWithEntries` (store mode), so every non-`session.json`
* entry is re-emitted with byte-identical uncompressed content.
*/
async function embedCoverageInSessionJson(zip, h3Cells, h3Resolution) {
	let sessionName;
	let session;
	const reader = new ZipReader(new BlobReader(zip));
	try {
		const sessionEntry = (await reader.getEntries()).find((e) => !e.directory && isSessionJsonEntryName(e.filename));
		if (!sessionEntry) {
			log.warn("No session.json found; leaving zip untouched");
			return zip;
		}
		sessionName = sessionEntry.filename;
		try {
			const parsed = JSON.parse(await sessionEntry.getData(new TextWriter()));
			if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
				log.warn("session.json is not an object; leaving zip untouched");
				return zip;
			}
			session = parsed;
		} catch (err) {
			log.warn("session.json unparseable; leaving zip untouched", err);
			return zip;
		}
	} catch (err) {
		log.warn("Failed to read zip for coverage embed; leaving untouched", err);
		return zip;
	} finally {
		await reader.close();
	}
	if (session.h3Cells !== void 0) return zip;
	const merged = {
		...session,
		h3Cells,
		h3Resolution
	};
	try {
		return await rebuildZipWithEntries(zip, [{
			path: sessionName,
			data: JSON.stringify(merged)
		}]);
	} catch (err) {
		log.warn("Failed to re-emit zip; leaving original untouched", err);
		return zip;
	}
}
//#endregion
export { embedCoverageInSessionJson };
